'use client';

import React from 'react';
import { useRoomContext } from '@livekit/components-react';
import { Hand } from 'lucide-react';
import { useHandRaise } from './HandRaiseContext';
import { useHandRaiseQueue } from './HandRaiseQueueContext';

interface HandRaiseButtonProps {
  inline?: boolean;
  className?: string;
  onToggle?: (raised: boolean) => void;
}

export const HandRaiseButton = React.forwardRef<HTMLButtonElement, HandRaiseButtonProps>(
  function HandRaiseButton({ inline = false, className, onToggle }: HandRaiseButtonProps, ref) {
    const room = useRoomContext();
    const { setHandRaised } = useHandRaise();
    const { addToQueue, removeFromQueue, getQueuePosition } = useHandRaiseQueue();
    const [isHandRaised, setIsHandRaised] = React.useState(false);

    const queuePosition = room?.localParticipant
      ? getQueuePosition(room.localParticipant.identity)
      : -1;

    const mergeLocalParticipantMetadata = React.useCallback(
      async (updates: Record<string, unknown>) => {
        const participant = room?.localParticipant;
        if (!participant) {
          return;
        }

        if (participant.permissions?.canUpdateMetadata === false) {
          return;
        }

        const currentMetadata = participant.metadata;
        let parsed: Record<string, any> = {};

        if (currentMetadata) {
          try {
            const candidate = JSON.parse(currentMetadata);
            if (candidate && typeof candidate === 'object') {
              parsed = candidate as Record<string, any>;
            }
          } catch (error) {
            console.warn(
              'HandRaiseButton: não foi possível interpretar metadata atual do participante local:',
              error,
            );
          }
        }

        const merged = {
          ...parsed,
          ...updates,
        };

        try {
          const serialized = JSON.stringify(merged);
          if (serialized !== currentMetadata) {
            await participant.setMetadata(serialized);
          }
        } catch (error) {
          console.warn('HandRaiseButton: erro ao atualizar metadata do participante local:', error);
        }
      },
      [room],
    );

    React.useEffect(() => {
      if (!room?.localParticipant) return;

      try {
        const metadata = room.localParticipant.metadata;
        if (metadata) {
          const parsed = JSON.parse(metadata);
          if (parsed.handRaised !== undefined) {
            setIsHandRaised(parsed.handRaised);
            setHandRaised(room.localParticipant.identity, parsed.handRaised);

            const participantName = room.localParticipant.name || room.localParticipant.identity;
            if (parsed.handRaised) {
              addToQueue(room.localParticipant.identity, participantName, parsed.timestamp);
            } else {
              removeFromQueue(room.localParticipant.identity);
            }
          }
        }
      } catch (e) {}
    }, [room?.localParticipant, setHandRaised, addToQueue, removeFromQueue]);

    React.useEffect(() => {
      if (!room) return;

      const handleManagementCommand = (payload: Uint8Array, participant: any) => {
        try {
          const data = JSON.parse(new TextDecoder().decode(payload));

          if (
            data.type === 'lower-hand-command' &&
            data.targetParticipant === room.localParticipant?.identity
          ) {
            // Host pediu para baixar nossa mão
            setIsHandRaised(false);
            setHandRaised(room.localParticipant.identity, false);
            removeFromQueue(room.localParticipant.identity);

            // Atualizar metadata preservando dados existentes
            void mergeLocalParticipantMetadata({ handRaised: false, timestamp: Date.now() });
          } else if (data.type === 'clear-all-hands') {
            // Host limpou todas as mãos
            setIsHandRaised(false);
            setHandRaised(room.localParticipant.identity, false);
            removeFromQueue(room.localParticipant.identity);

            // Atualizar metadata preservando dados existentes
            void mergeLocalParticipantMetadata({ handRaised: false, timestamp: Date.now() });
          }
        } catch (e) {
          console.error('Erro ao processar comando de gerenciamento:', e);
        }
      };

      room.on('dataReceived', handleManagementCommand);

      return () => {
        room.off('dataReceived', handleManagementCommand);
      };
    }, [room, setHandRaised, removeFromQueue, mergeLocalParticipantMetadata]);

    // Função para notificar outros participantes sobre mudança de estado
    const notifyHandRaiseChange = (raised: boolean) => {
      // Sempre enviar via data channel para outros participantes
      publishHandRaiseViaDataChannel(raised);

      // Se tiver permissão, também atualizar metadata (no cliente a flag é canUpdateMetadata)
      void mergeLocalParticipantMetadata({ handRaised: raised, timestamp: Date.now() });
    };

    const toggleHandRaise = () => {
      const next = !isHandRaised;

      // Atualiza estado local primeiro
      setIsHandRaised(next);

      // Atualizar o contexto compartilhado (não dentro do updater do setState)
      if (room?.localParticipant) {
        setHandRaised(room.localParticipant.identity, next);

        // Gerenciar fila de mãos levantadas
        const participantName = room.localParticipant.name || room.localParticipant.identity;
        if (next) {
          addToQueue(room.localParticipant.identity, participantName);
        } else {
          removeFromQueue(room.localParticipant.identity);
        }
      }

      // Notificar outros participantes
      notifyHandRaiseChange(next);
      onToggle?.(next);
    };

    const publishHandRaiseViaDataChannel = (raised: boolean) => {
      try {
        const data = {
          type: 'hand-raise',
          raised,
          id: room?.localParticipant.identity,
          timestamp: Date.now(),
        };

        room?.localParticipant
          .publishData(new TextEncoder().encode(JSON.stringify(data)), { topic: 'hand-raise' })
          .catch((error) => {
            console.error('Erro ao enviar data channel:', error);
          });
      } catch (error) {
        console.error('Erro ao publicar via data channel:', error);
      }
    };

    return (
      <button
        type="button"
        aria-pressed={isHandRaised}
        onClick={toggleHandRaise}
        data-hand-raise-button
        className={className}
        ref={ref}
        style={{
          width: inline ? '44px' : 'auto',
          height: inline ? '44px' : 'auto',
          padding: inline ? '0px' : undefined,
          borderRadius: inline ? '8px' : undefined,
          background: isHandRaised ? '#ffd700' : inline ? 'var(--lk-bg2)' : undefined,
          color: inline ? 'var(--lk-fg)' : undefined,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: 'none',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
        }}
        onMouseEnter={(e) => {
          if (!isHandRaised) {
            e.currentTarget.style.background = 'var(--lk-bg3, rgba(255,255,255,0.1))';
          }
        }}
        onMouseLeave={(e) => {
          if (!isHandRaised) {
            e.currentTarget.style.background = 'var(--lk-bg2)';
          }
        }}
        title={isHandRaised && queuePosition > 0 ? `Posição na fila: ${queuePosition}` : undefined}
      >
        <Hand
          size={inline ? 20 : 16}
          className={`transition-transform duration-200 ${isHandRaised ? 'rotate-12' : ''}`}
          style={{
            color: isHandRaised ? '#000000' : 'currentColor',
            filter: isHandRaised ? 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))' : 'none',
          }}
          strokeWidth={isHandRaised ? 1.5 : 2.5}
        />
        {!inline && <span style={{ marginLeft: '8px' }}>Levantar Mão</span>}

        {/* Indicador de posição na fila */}
        {isHandRaised && queuePosition > 0 && inline && (
          <div
            className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center"
            style={{
              fontSize: '10px',
              lineHeight: '1',
              fontWeight: 'bold',
            }}
          >
            {queuePosition}
          </div>
        )}
      </button>
    );
  },
);

HandRaiseButton.displayName = 'HandRaiseButton';
